import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Blinder from '../../common/blinder/blinder';
import ConsentSensitive from '../../common/consent-sensitive/consent-sensitive';
import CountrySpecific from '../../common/country-specific/country-specific';
import ZendeskWidget from '../../common/zendesk-widget/zendesk-widget';
import { LATINA_LIST } from '../../constants/latina-country';
import { QUERY_MATERIAL } from '../../constants/query-params/material';
import { QUERY_PAYMENT } from '../../constants/query-params/payment';
import useIsMobile from '../../hooks/use-is-mobile';
import translation from '../../languages';
import { AVAILABLE_WORLDS } from '../../languages/constants';
import { changeUserDetails } from '../../reducers/user';
import usePaymentOptions from '../../services/payment/use-payment-options';
import { USERCENTRICS_SERVICES } from '../../services/usercentrics';
import { storageKeys } from '../../utils/browser-storage/storage-keys';
import formatPriceToFloat from '../../utils/price/format-price-to-float';
import serializeQueryParams from '../../utils/url/serialize-query-params';
import CartInfo from './components/cart-info/cart-info';
import DefaultPaymentPage from './components/default-payment-page/default-payment-page';
import ErrorMessage from './components/error-message/error-message';
import Footer from './components/footer/footer';
import Header from './components/header/header';
import KushkiPaymentPage from './components/kushki/kushki-payment-page';
import NotAvailablePage from './components/not-available-page/not-available-page';
import PurchaseForm from './components/purchase-form/purchase-form';
import { PURCHASE_FORM_MODE } from './components/purchase-form/purchase-form.constants';
import useHasSelectedPaymentMethod from './hooks/use-has-selected-payment-method';
import usePaymentCart from './hooks/use-payment-cart';
import usePaymentOptionsAvailability from './hooks/use-payment-options-availability';
import useSyncPaymentOptionsError from './hooks/use-sync-payment-options-error';
import { isKushkiPayment } from './payment.helpers';
import { addPaymentError, removePaymentError } from './reducers';
import { initCheckoutSourceData } from '../../services/quality-events/quality-events-pages/checkout-page-quality-events';
import './payment.scss';

const Payment: FC<TranslationProps> = ({ t }) => {
    const search = serializeQueryParams();
    const { isDataProcessing, paymentErrors, paymentCart } = useSelector((state: RootState) => state.payment);
    const currency = useSelector((state: RootState) => state.main.currency);
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const geotargetting = useSelector((state: RootState) => state.main.geotargeting);
    const dispatch = useDispatch();
    const hasSelectedPaymentMethod = useHasSelectedPaymentMethod();
    const [cartFinalPrice, setCartFinalPrice] = useState(0);
    const { data: paymentOptionsData, isError: hasPaymentOptionsError } = usePaymentOptions(currency.code);

    usePaymentOptionsAvailability(paymentOptionsData);
    useSyncPaymentOptionsError(hasPaymentOptionsError);
    usePaymentCart();

    useEffect(() => {
        if (search[QUERY_PAYMENT.ORDER_TOKEN]) {
            const reloadRL = new URL(window.location.href);

            reloadRL.searchParams.delete(QUERY_PAYMENT.ORDER_TOKEN);
            navigate(reloadRL.href, { replace: true });

            return;
        }

        if (search[QUERY_PAYMENT.ERROR]) {
            dispatch(addPaymentError({}));
        }

        // Reset data about referral promo
        window.storage.removeItem(storageKeys.cart.referralPromoMaterial);

        initCheckoutSourceData();
    }, []);

    useEffect(() => {
        if (paymentCart?.loaded) {
            setCartFinalPrice(formatPriceToFloat(paymentCart?.finalPrice) || 0);
        }

        if (paymentCart?.promo?.applied) {
            window.storage.setItem(storageKeys.cart.referralPromoMaterial, paymentCart?.promo?.applied);
        }
    }, [paymentCart?.loaded]);

    useEffect(() => {
        if (!paymentOptionsData?.loaded) {
            return;
        }

        dispatch(changeUserDetails({ paypalVaultIdToken: paymentOptionsData.paypalCheckout?.id_token }));
    }, [paymentOptionsData]);

    const onCloseErrorMessage = (errorKey: number) => {
        dispatch(removePaymentError(errorKey));
    };

    if (paymentOptionsData?.userCountry === 'CU') {
        return <NotAvailablePage />;
    }

    return (
        <div>
            <Header />
            <div className='lmp-payment regular-page-container position-relative notranslate'>
                {(isDataProcessing || (!paymentOptionsData?.loaded && !hasPaymentOptionsError)) && <Blinder />}
                {Array.from(paymentErrors?.keys() ?? []).map((errorKey) => (
                    <ErrorMessage
                        key={errorKey}
                        error={paymentErrors?.get(errorKey) ?? {}}
                        onClose={() => onCloseErrorMessage(errorKey)}
                    />
                ))}
                <>
                    {Boolean(
                        paymentOptionsData &&
                            !isKushkiPayment(paymentOptionsData) &&
                            !paymentOptionsData?.showFallbackMethods
                    ) && <div className='tsc-h4 selection-title'>{t('payment.paymentMethodSelection.title')}</div>}
                    <div className='row'>
                        <div className='col-xs-12 col-sm-12 col-md-8'>
                            <>
                                {paymentOptionsData && isKushkiPayment(paymentOptionsData) && (
                                    <>
                                        {!hasSelectedPaymentMethod && !isMobile && (
                                            <ErrorMessage
                                                error={{
                                                    message: t('payment.paymentMethodSelection.noPaymentMethodSelected')
                                                }}
                                            />
                                        )}
                                        <KushkiPaymentPage />
                                    </>
                                )}
                                {paymentOptionsData && !isKushkiPayment(paymentOptionsData) && (
                                    <>
                                        <DefaultPaymentPage />
                                        {!hasSelectedPaymentMethod && !isMobile && (
                                            <ErrorMessage
                                                error={{
                                                    message: t('payment.paymentMethodSelection.noPaymentMethodSelected')
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                                <PurchaseForm
                                    mode={cartFinalPrice > 0 ? PURCHASE_FORM_MODE.PAID : PURCHASE_FORM_MODE.FREE}
                                />
                            </>
                        </div>

                        <div className='col-xs-12 col-sm-12 col-md-4 right-block'>
                            {(paymentOptionsData || hasPaymentOptionsError) && <CartInfo />}
                        </div>
                    </div>
                </>
            </div>
            {LATINA_LIST.includes(geotargetting.countryCode) && (
                <ConsentSensitive type={USERCENTRICS_SERVICES.ZENDESK}>
                    <CountrySpecific allowed={AVAILABLE_WORLDS.ES}>
                        <ZendeskWidget />
                    </CountrySpecific>
                </ConsentSensitive>
            )}
            <Footer />
        </div>
    );
};

const translate = translation('payment');

export default translate(Payment);
